import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, Navigation } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TripForm = ({ open, onClose, onSave, trip, mode = 'create' }) => {
  const [vessels, setVessels] = useState([]);
  const [tripTypes, setTripTypes] = useState([]);
  const [tripPassengers, setTripPassengers] = useState([]);
  const [allocatedCrew, setAllocatedCrew] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [gpsLoading, setGpsLoading] = useState({ depart: false, arrival: false });

  const [formData, setFormData] = useState({
    trip_name: '',
    vessel_id: '',
    trip_type: '',
    operating_area: '',
    planned_depart_datetime: '',
    planned_arrival_datetime: '',
    actual_depart_datetime: '',
    actual_arrival_datetime: '',
    depart_location: '',
    arrival_location: '',
    gps_depart_location: '',
    gps_arrival_location: '',
    number_of_passengers: 0,
    number_of_crew: 0
  });

  const fetchVessels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vessels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVessels(response.data);
    } catch (err) {
      console.error('Error fetching vessels:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch trip types
      const tripTypesRes = await fetch(`${API}/settings/trip/trip_types`, { headers });
      const tripTypesData = await tripTypesRes.json();
      setTripTypes(tripTypesData.options || []);
      
    } catch (err) {
      console.error('Error fetching settings:', err);
      setTripTypes([]);
    }
  };

  const fetchTripPassengers = async (tripId) => {
    if (!tripId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trip-passengers?trip_id=${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTripPassengers(response.data || []);
    } catch (err) {
      console.error('Error fetching trip passengers:', err);
    }
  };

  const fetchAllocatedCrew = async (tripId) => {
    if (!tripId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/allocated-crew?trip_id=${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllocatedCrew(response.data || []);
    } catch (err) {
      console.error('Error fetching allocated crew:', err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchVessels();
      fetchSettings();
    }
  }, [open]);

  useEffect(() => {
    if (trip && mode === 'edit') {
      setFormData({
        trip_name: trip.trip_name || '',
        vessel_id: trip.vessel_id || '',
        trip_type: trip.trip_type || '',
        operating_area: trip.operating_area || '',
        planned_depart_datetime: trip.planned_depart_datetime || '',
        planned_arrival_datetime: trip.planned_arrival_datetime || '',
        actual_depart_datetime: trip.actual_depart_datetime || '',
        actual_arrival_datetime: trip.actual_arrival_datetime || '',
        depart_location: trip.depart_location || '',
        arrival_location: trip.arrival_location || '',
        gps_depart_location: trip.gps_depart_location || '',
        gps_arrival_location: trip.gps_arrival_location || '',
        number_of_passengers: trip.number_of_passengers || 0,
        number_of_crew: trip.number_of_crew || 0
      });
      fetchTripPassengers(trip.id);
      fetchAllocatedCrew(trip.id);
    } else if (mode === 'create') {
      setFormData({
        trip_name: '',
        vessel_id: '',
        trip_type: '',
        operating_area: '',
        planned_depart_datetime: '',
        planned_arrival_datetime: '',
        actual_depart_datetime: '',
        actual_arrival_datetime: '',
        depart_location: '',
        arrival_location: '',
        gps_depart_location: '',
        gps_arrival_location: '',
        number_of_passengers: 0,
        number_of_crew: 0
      });
      setTripPassengers([]);
      setAllocatedCrew([]);
    }
    setError('');
    setMessage('');
  }, [trip, mode, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // GPS Location lookup function
  const getGPSLocation = async (field) => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    const loadingKey = field === 'gps_depart_location' ? 'depart' : 'arrival';
    setGpsLoading(prev => ({ ...prev, [loadingKey]: true }));
    setError('');

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      if (permissionStatus.state === 'denied') {
        setError('Location access is blocked. Please allow location access in your browser settings.');
        setGpsLoading(prev => ({ ...prev, [loadingKey]: false }));
        return;
      }
    } catch (e) {
      console.log('Permissions API not supported, proceeding with geolocation request');
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`;
        setFormData(prev => ({ ...prev, [field]: coords }));
        setMessage(`GPS location captured: ${coords}`);
        setTimeout(() => setMessage(''), 3000);
        setGpsLoading(prev => ({ ...prev, [loadingKey]: false }));
      },
      (err) => {
        let errorMessage = 'Unable to retrieve location';
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please allow location access and try again.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please check your device location services.';
            break;
          case err.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = `Location error: ${err.message || 'Unknown error'}`;
        }
        setError(errorMessage);
        setTimeout(() => setError(''), 5000);
        setGpsLoading(prev => ({ ...prev, [loadingKey]: false }));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = () => {
    if (!formData.trip_name.trim()) {
      setError('Trip name is required');
      return;
    }
    if (!formData.vessel_id) {
      setError('Please select a vessel');
      return;
    }
    setError('');
    
    // Update passenger and crew counts based on allocated data
    const updatedFormData = {
      ...formData,
      number_of_passengers: tripPassengers.length,
      number_of_crew: allocatedCrew.length
    };
    
    onSave(updatedFormData);
  };

  // Format date for display in header
  const formatDateForHeader = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get the departure date from trip (handle both field names)
  const getDepartureDate = () => {
    const dateStr = trip?.planned_depart_datetime || trip?.depart_datetime;
    return dateStr ? formatDateForHeader(dateStr) : '';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Trip' : `Edit Trip${trip?.trip_name ? ` - ${trip.trip_name}` : ''}${getDepartureDate() ? ` - ${getDepartureDate()}` : ''}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Add a new trip to the system' : 'Update trip details'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription className="text-green-800">{message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* Trip Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trip_name">Trip Name *</Label>
                  <Input
                    id="trip_name"
                    value={formData.trip_name}
                    onChange={(e) => handleChange('trip_name', e.target.value)}
                    placeholder="Enter trip name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vessel_id">Vessel *</Label>
                  <Select value={formData.vessel_id} onValueChange={(value) => handleChange('vessel_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vessel" />
                    </SelectTrigger>
                    <SelectContent>
                      {vessels.map(vessel => (
                        <SelectItem key={vessel.id} value={vessel.id}>
                          {vessel.vessel_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trip_type">Trip Type</Label>
                  <Select value={formData.trip_type} onValueChange={(value) => handleChange('trip_type', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select trip type" />
                    </SelectTrigger>
                    <SelectContent>
                      {tripTypes.map((type, idx) => (
                        <SelectItem key={idx} value={typeof type === 'string' ? type : type.value}>
                          {typeof type === 'string' ? type : type.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="operating_area">Operating Area</Label>
                  <Input
                    id="operating_area"
                    value={formData.operating_area}
                    onChange={(e) => handleChange('operating_area', e.target.value)}
                    placeholder="Enter operating area"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="depart_location" className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    Departure Location
                  </Label>
                  <Input
                    id="depart_location"
                    value={formData.depart_location}
                    onChange={(e) => handleChange('depart_location', e.target.value)}
                    placeholder="Enter departure location"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="arrival_location" className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    Arrival Location
                  </Label>
                  <Input
                    id="arrival_location"
                    value={formData.arrival_location}
                    onChange={(e) => handleChange('arrival_location', e.target.value)}
                    placeholder="Enter arrival location"
                  />
                </div>
              </div>

              {/* GPS Location Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gps_depart_location" className="flex items-center gap-1">
                    <Navigation className="h-4 w-4" />
                    GPS Departure Location
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="gps_depart_location"
                      value={formData.gps_depart_location}
                      onChange={(e) => handleChange('gps_depart_location', e.target.value)}
                      placeholder="Lat, Long"
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => getGPSLocation('gps_depart_location')}
                      disabled={gpsLoading.depart}
                      className="whitespace-nowrap"
                    >
                      {gpsLoading.depart ? (
                        <span className="animate-pulse">Getting...</span>
                      ) : (
                        <>📍 Get GPS</>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gps_arrival_location" className="flex items-center gap-1">
                    <Navigation className="h-4 w-4" />
                    GPS Arrival Location
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="gps_arrival_location"
                      value={formData.gps_arrival_location}
                      onChange={(e) => handleChange('gps_arrival_location', e.target.value)}
                      placeholder="Lat, Long"
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => getGPSLocation('gps_arrival_location')}
                      disabled={gpsLoading.arrival}
                      className="whitespace-nowrap"
                    >
                      {gpsLoading.arrival ? (
                        <span className="animate-pulse">Getting...</span>
                      ) : (
                        <>📍 Get GPS</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="planned_depart">Planned Departure</Label>
                  <Input
                    id="planned_depart"
                    type="datetime-local"
                    value={formData.planned_depart_datetime}
                    onChange={(e) => handleChange('planned_depart_datetime', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="planned_arrival">Planned Arrival</Label>
                  <Input
                    id="planned_arrival"
                    type="datetime-local"
                    value={formData.planned_arrival_datetime}
                    onChange={(e) => handleChange('planned_arrival_datetime', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="actual_depart">Actual Departure</Label>
                  <Input
                    id="actual_depart"
                    type="datetime-local"
                    value={formData.actual_depart_datetime}
                    onChange={(e) => handleChange('actual_depart_datetime', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="actual_arrival">Actual Arrival</Label>
                  <Input
                    id="actual_arrival"
                    type="datetime-local"
                    value={formData.actual_arrival_datetime}
                    onChange={(e) => handleChange('actual_arrival_datetime', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Crew</Label>
                  <div className="text-2xl font-bold text-green-600">{allocatedCrew.length}</div>
                  <p className="text-xs text-gray-500">Manage in Trip Details view</p>
                </div>
                <div className="space-y-2">
                  <Label>Passengers</Label>
                  <div className="text-2xl font-bold text-blue-600">{tripPassengers.length}</div>
                  <p className="text-xs text-gray-500">Manage in Trip Details view</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {mode === 'create' ? 'Create Trip' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};

export default TripForm;
